/* ===========================================================================
 * Coupon Popup
 * ---------------------------------------------------------------------------
 * Popup de cupom ancorado num canto da tela. NÃO é modal: não cria overlay,
 * não trava o scroll e não move o foco — a navegação segue livre.
 *
 * Ciclo de vida:
 *   1. Confere a frequência de exibição (sessão / a cada X minutos ou dias,
 *      guardada em sessionStorage / localStorage) — ou a ignora na página
 *      inicial, se "sempre na página inicial" estiver ligado — e espera o
 *      atraso configurado.
 *   2. Exibe com transição CSS e inicia a contagem regressiva.
 *   3. Fecha no X, com Esc (foco dentro do popup) ou ao fim da contagem.
 *
 * Gatilho do carrinho (opção da section Cart drawer): na PRIMEIRA vez que o
 * <cart-drawer> abre, o popup aparece na hora, independente da frequência.
 * A abertura é detectada observando a classe `is-visible` do drawer — sem
 * tocar no cart-drawer.js.
 *
 * A contagem regressiva É a animação CSS da barra de progresso: o popup fecha
 * no `animationend` dela. Assim barra e timer nunca divergem, e pausar
 * (hover, foco via teclado, aba em segundo plano) é só um
 * `animation-play-state: paused` — sem setInterval nem cálculo de tempo.
 *
 * Isolamento: nada global além do custom element, nenhuma dependência de
 * scripts do tema, e os listeners de `document` e timers são removidos no
 * disconnectedCallback (o editor de temas re-renderiza a section).
 * =========================================================================== */

class CouponPopup extends HTMLElement {
  constructor() {
    super();

    this.onCloseClick = this.onCloseClick.bind(this);
    this.onCopyClick = this.onCopyClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onCountdownEnd = this.onCountdownEnd.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onEditorEvent = this.onEditorEvent.bind(this);
    this.onCartDrawerMutation = this.onCartDrawerMutation.bind(this);

    this.isOpen = false;
    this.pendingShow = false;
    this.pauseReasons = new Set();
    this.timers = {};
    this.cartObserver = null;
  }

  connectedCallback() {
    this.progressBar = this.querySelector('[data-coupon-popup-progress]');
    this.closeButton = this.querySelector('[data-coupon-popup-close]');
    this.copyButton = this.querySelector('[data-coupon-popup-copy]');
    this.copyHint = this.querySelector('[data-coupon-popup-copy-hint]');

    this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

    this.closeButton.addEventListener('click', this.onCloseClick);
    this.progressBar.addEventListener('animationend', this.onCountdownEnd);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    if (this.copyButton) this.copyButton.addEventListener('click', this.onCopyClick);

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Independe da frequência: vale mesmo que o popup não vá aparecer sozinho.
    this.initCartTrigger();

    if (this.isDesignMode) {
      this.initDesignMode();
      return;
    }

    if (!this.shouldShow()) return;

    this.timers.show = setTimeout(() => this.requestShow(), this.delay);
  }

  disconnectedCallback() {
    Object.values(this.timers).forEach(timer => clearTimeout(timer));

    this.closeButton.removeEventListener('click', this.onCloseClick);
    this.progressBar.removeEventListener('animationend', this.onCountdownEnd);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    if (this.copyButton) this.copyButton.removeEventListener('click', this.onCopyClick);

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:section:select', this.onEditorEvent);
    document.removeEventListener('shopify:section:load', this.onEditorEvent);
    document.removeEventListener('shopify:section:deselect', this.onEditorEvent);

    if (this.cartObserver) this.cartObserver.disconnect();
  }

  /* -------------------------------------------------------------------------
   * Configuração (data-attributes escritos pelo Liquid)
   * ---------------------------------------------------------------------- */

  get delay() {
    return (Number(this.dataset.delay) || 0) * 1000;
  }

  get animationDuration() {
    return Number(this.dataset.animationDuration) || 0;
  }

  get storageKey() {
    return `coupon-popup:${this.dataset.storageKey}`;
  }

  /** Memória do gatilho do carrinho — também por cupom. */
  get cartStorageKey() {
    return `${this.storageKey}:cart`;
  }

  /** Intervalo das frequências "a cada X minutos/dias" (o Liquid envia em minutos). */
  get frequencyInterval() {
    return (Number(this.dataset.frequencyInterval) || 0) * CouponPopup.MINUTE_MS;
  }

  /* -------------------------------------------------------------------------
   * Exibir / fechar
   * ---------------------------------------------------------------------- */

  /**
   * Se o atraso terminar com a aba em segundo plano, a exibição espera a aba
   * voltar — senão o visitante perderia a entrada e parte do tempo.
   */
  requestShow() {
    if (document.hidden) {
      this.pendingShow = true;
      return;
    }

    this.show();
  }

  show() {
    this.pendingShow = false;
    if (this.isOpen) return;

    clearTimeout(this.timers.hide);
    this.isOpen = true;
    this.hidden = false;
    this.markAsSeen();

    // Força o navegador a registrar o estado inicial (opacity 0 + transform)
    // antes de aplicar o visível; sem isso não há transição de entrada.
    void this.offsetWidth;
    this.classList.add('is-visible');
    this.startCountdown();
  }

  hide() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove('is-visible');

    // Congela a barra durante a saída (em vez de reiniciá-la).
    this.pause('closed');

    // `hidden` só depois da transição de saída. Timeout em vez de
    // `transitionend`: fecha mesmo com reduced-motion, aba em segundo plano
    // ou transição interrompida.
    this.timers.hide = setTimeout(() => {
      this.hidden = true;
      // Só agora, já invisível, o popup volta à posição normal (sem "pulo").
      this.classList.remove('coupon-popup--cart');
    }, this.animationDuration);
  }

  onCloseClick() {
    this.hide();
  }

  /** Esc só fecha quando o foco está no popup: não disputa o Esc do tema. */
  onKeydown(event) {
    if (event.key === 'Escape') this.hide();
  }

  /* -------------------------------------------------------------------------
   * Contagem regressiva (= animação da barra de progresso)
   * ---------------------------------------------------------------------- */

  startCountdown() {
    this.pauseReasons.clear();
    if (document.hidden) this.pauseReasons.add('page-hidden');
    this.syncPaused();

    // Remove e reaplica a classe para a animação recomeçar do zero.
    this.classList.remove('is-counting');
    void this.progressBar.offsetWidth;
    this.classList.add('is-counting');
  }

  onCountdownEnd(event) {
    if (event.target !== this.progressBar) return;

    // No editor o popup não fecha sozinho: a barra roda em loop.
    if (this.isDesignMode) {
      this.startCountdown();
      return;
    }

    this.hide();
  }

  /**
   * Vários motivos podem pausar ao mesmo tempo (ex.: mouse em cima E aba
   * oculta); o tempo só volta a correr quando todos saírem.
   */
  pause(reason) {
    this.pauseReasons.add(reason);
    this.syncPaused();
  }

  resume(reason) {
    this.pauseReasons.delete(reason);
    this.syncPaused();
  }

  syncPaused() {
    this.classList.toggle('is-paused', this.pauseReasons.size > 0);
  }

  /** Só mouse: no toque, `pointerleave` nem sempre dispara e o popup travaria. */
  onPointerEnter(event) {
    if (event.pointerType === 'mouse' && this.dataset.pauseOnHover === 'true') {
      this.pause('hover');
    }
  }

  onPointerLeave(event) {
    if (event.pointerType === 'mouse') this.resume('hover');
  }

  /**
   * Foco via teclado sempre pausa (o tempo não pode acabar enquanto alguém
   * navega pelo popup). `:focus-visible` exclui o foco gerado por toque/clique.
   */
  onFocusIn(event) {
    if (event.target.matches(':focus-visible')) this.pause('focus');
  }

  onFocusOut(event) {
    if (!this.contains(event.relatedTarget)) this.resume('focus');
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.pause('page-hidden');
      return;
    }

    this.resume('page-hidden');
    if (this.pendingShow) this.show();
  }

  /* -------------------------------------------------------------------------
   * Copiar cupom
   * ---------------------------------------------------------------------- */

  onCopyClick() {
    this.copyText(this.copyButton.dataset.code).then(copied => {
      if (!copied) return;

      this.copyButton.classList.add('is-copied');
      this.copyHint.textContent = this.copyHint.dataset.copiedLabel;

      clearTimeout(this.timers.copy);
      this.timers.copy = setTimeout(() => {
        this.copyButton.classList.remove('is-copied');
        this.copyHint.textContent = this.copyHint.dataset.copyLabel;
      }, CouponPopup.COPY_FEEDBACK_MS);
    });
  }

  copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard
        .writeText(text)
        .then(() => true, () => this.copyTextFallback(text));
    }

    return Promise.resolve(this.copyTextFallback(text));
  }

  /** Navegadores sem Clipboard API, ou com a permissão negada. */
  copyTextFallback(text) {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    // 16px evita o zoom automático do iOS ao focar o campo.
    field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px;';
    document.body.appendChild(field);
    field.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }

    field.remove();
    this.copyButton.focus({ preventScroll: true });

    return copied;
  }

  /* -------------------------------------------------------------------------
   * Frequência de exibição
   * ---------------------------------------------------------------------- */

  shouldShow() {
    // "Sempre na página inicial": o Liquid só liga isto na home e nunca para
    // clientes logados que já compraram.
    if (this.dataset.alwaysShow === 'true') return true;

    switch (this.dataset.frequency) {
      case 'session':
        return this.readStorage('sessionStorage') === null;

      case 'minutes':
      case 'days': {
        const lastSeen = Number(this.readStorage('localStorage'));
        return !lastSeen || Date.now() - lastSeen >= this.frequencyInterval;
      }

      default:
        return true;
    }
  }

  /**
   * Marca na EXIBIÇÃO (não no fechamento): trocar de página não o reexibe.
   * Exibições forçadas pela página inicial também contam para as demais páginas.
   */
  markAsSeen() {
    if (this.isDesignMode) return;

    switch (this.dataset.frequency) {
      case 'session':
        this.writeStorage('sessionStorage', '1');
        break;

      case 'minutes':
      case 'days':
        this.writeStorage('localStorage', String(Date.now()));
        break;
    }
  }

  /**
   * O storage pode lançar exceção (navegação privada, cookies bloqueados).
   * Sem ele o popup apenas perde a memória e aparece normalmente.
   */
  readStorage(type, key = this.storageKey) {
    try {
      return window[type].getItem(key);
    } catch (error) {
      return null;
    }
  }

  writeStorage(type, value, key = this.storageKey) {
    try {
      window[type].setItem(key, value);
    } catch (error) {
      // Sem storage disponível: segue sem memória de exibição.
    }
  }

  /* -------------------------------------------------------------------------
   * Gatilho do carrinho
   * ---------------------------------------------------------------------- */

  /**
   * Liga o gatilho se o cart drawer tiver a opção ativa (atributo
   * `data-coupon-popup-on-open`, escrito pela section Cart drawer) e ele
   * ainda não tiver disparado para este cupom. Pode ser chamado de novo: o
   * editor de temas re-renderiza o drawer quando a opção muda.
   */
  initCartTrigger() {
    if (this.cartObserver) this.cartObserver.disconnect();

    this.cartDrawer = document.querySelector('cart-drawer[data-coupon-popup-on-open]');
    if (!this.cartDrawer) return;

    // Fora do editor, só a primeira abertura conta.
    if (!this.isDesignMode && this.readStorage('localStorage', this.cartStorageKey) !== null) {
      return;
    }

    this.isCartOpen = this.cartDrawer.classList.contains('is-visible');
    this.cartObserver = new MutationObserver(this.onCartDrawerMutation);
    this.cartObserver.observe(this.cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /** O drawer abre e fecha alternando a classe `is-visible` (cart-drawer.js). */
  onCartDrawerMutation() {
    const isCartOpen = this.cartDrawer.classList.contains('is-visible');
    if (isCartOpen === this.isCartOpen) return;
    this.isCartOpen = isCartOpen;

    if (isCartOpen) {
      this.showForCart();
    } else if (this.isDesignMode) {
      // No editor o popup não fecha sozinho: volta à posição normal na hora.
      this.classList.remove('coupon-popup--cart');
    }
  }

  /**
   * Exibe já na posição do carrinho (ver section-coupon-popup.css). Se o popup
   * estava visível em outro canto, a entrada recomeça no novo lugar em vez de
   * ele "pular" de posição, e a contagem recomeça do zero.
   */
  showForCart() {
    if (!this.isDesignMode) {
      this.cartObserver.disconnect();
      this.writeStorage('localStorage', '1', this.cartStorageKey);
    }

    // Cancela a exibição agendada pelo atraso: senão ela reabriria o popup
    // depois que o cliente o fechasse.
    clearTimeout(this.timers.show);
    this.pendingShow = false;

    // Volta ao estado oculto instantaneamente (sem transição), já no novo canto.
    this.isOpen = false;
    this.style.transition = 'none';
    this.classList.remove('is-visible');
    this.classList.add('coupon-popup--cart');
    void this.offsetWidth;
    this.style.transition = '';

    this.show();
  }

  /* -------------------------------------------------------------------------
   * Editor de temas
   * ---------------------------------------------------------------------- */

  /**
   * No editor o popup aparece sem atraso, ignora a frequência (nada é gravado
   * no storage) e a barra roda em loop, para o lojista ver as alterações ao
   * vivo. `section:load` cobre o re-render feito a cada ajuste de configuração.
   */
  initDesignMode() {
    document.addEventListener('shopify:section:select', this.onEditorEvent);
    document.addEventListener('shopify:section:load', this.onEditorEvent);
    document.addEventListener('shopify:section:deselect', this.onEditorEvent);

    if (this.dataset.designPreview === 'true') this.show();
  }

  onEditorEvent(event) {
    // O cart drawer pode ter sido re-renderizado (ex.: a opção do popup acabou
    // de ser ligada ou desligada), e com ele o elemento observado.
    if (event.type === 'shopify:section:load') this.initCartTrigger();

    if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;

    if (event.type === 'shopify:section:deselect') {
      if (this.dataset.designPreview !== 'true') this.hide();
      return;
    }

    this.show();
  }
}

CouponPopup.COPY_FEEDBACK_MS = 2000;
CouponPopup.MINUTE_MS = 60 * 1000;

if (!customElements.get('coupon-popup')) {
  customElements.define('coupon-popup', CouponPopup);
}
